import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VolunteerDashboard from "./pages/VolunteerDashboard";
import SupervisorDashboard from "./pages/SupervisorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerSignup from "./pages/CustomerSignup";
import ClubApplicationForm from "./pages/ClubApplicationForm";
import CompanyApplicationForm from "./pages/CompanyApplicationForm";
import PendingApproval from "./pages/PendingApproval";
import MarketingHome from "./pages/MarketingHome";
import ClubsPage from "./pages/ClubsPage";
import CompaniesPage from "./pages/CompaniesPage";
import ClubsFAQ from "./pages/ClubsFAQ";
import CompaniesFAQ from "./pages/CompaniesFAQ";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import TokenViewer from "./pages/TokenViewer";
import ClubGuide from "./pages/ClubGuide";
import TestCredentials from "./pages/TestCredentials";
import WorkflowGuide from "./pages/WorkflowGuide";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .get(`${API}/auth/me`)
        .then((response) => {
          setUser(response.data);
        })
        .catch(() => {
          localStorage.removeItem("token");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={!user ? <Login setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/signup/:role/:clubToken?" element={!user ? <Signup setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/customer-signup" element={!user ? <CustomerSignup /> : <Navigate to="/" />} />
          <Route path="/apply/club" element={<ClubApplicationForm />} />
          <Route path="/apply/company" element={<CompanyApplicationForm />} />
          <Route path="/pending-approval" element={!user ? <PendingApproval /> : <Navigate to="/" />} />
          
          <Route
            path="/"
            element={
              user ? (
                user.role === "volunteer" ? (
                  <Navigate to="/dashboard/volunteer" replace />
                ) : user.role === "supervisor" ? (
                  <Navigate to="/dashboard/supervisor" replace />
                ) : user.role === "admin" ? (
                  <Navigate to="/dashboard/admin" replace />
                ) : user.role === "customer" ? (
                  <Navigate to="/dashboard/customer" replace />
                ) : (
                  <Navigate to="/login" />
                )
              ) : (
                <MarketingHome />
              )
            }
          />
          
          {/* Role-specific dashboard routes */}
          <Route path="/dashboard/volunteer" element={user && user.role === "volunteer" ? <VolunteerDashboard user={user} logout={logout} /> : <Navigate to="/login" />} />
          <Route path="/dashboard/supervisor" element={user && user.role === "supervisor" ? <SupervisorDashboard user={user} logout={logout} /> : <Navigate to="/login" />} />
          <Route path="/dashboard/admin" element={user && user.role === "admin" ? <AdminDashboard user={user} logout={logout} /> : <Navigate to="/login" />} />
          <Route path="/dashboard/customer" element={user && user.role === "customer" ? <CustomerDashboard user={user} logout={logout} /> : <Navigate to="/login" />} />
          
          {/* Marketing Pages - accessible to everyone */}
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/clubs-faq" element={<ClubsFAQ />} />
          <Route path="/companies-faq" element={<CompaniesFAQ />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          
          {/* Token Viewer - public page */}
          <Route path="/token/:tokenId" element={<TokenViewer />} />
          
          {/* Club Guide - public page */}
          <Route path="/club-guide" element={<ClubGuide />} />
          
          {/* Test Credentials - public page for testing */}
          <Route path="/test-credentials" element={<TestCredentials />} />
          
          {/* Workflow Guide - public page */}
          <Route path="/workflow" element={<WorkflowGuide />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
